import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles } from 'lucide-react';

const TRIAL_FEATURES = [
  'Full access to LISTING AiGENT for 7 days',
  'Unlimited AI call answering',
  'Import listings from Zillow, Redfin, Realtor',
  'Auto-scheduling into your calendar',
  'Full call transcripts & summaries',
  'Cancel anytime before the trial ends — no monthly charge',
];

const PRO_FEATURES = [
  'Everything in trial',
  'Unlimited listings',
  'SMS lead follow-ups',
  'Team seats & shared inbox',
  'Call analytics & sentiment reports',
  'Priority support',
];

function PricingHero() {
  return (
    <section className="bg-brand-green text-brand-cream py-20 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-green via-brand-green-dark to-brand-green-darker opacity-70" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Pricing</span>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-tight">Simple, honest pricing.</h1>
        <p className="mt-6 text-lg text-brand-cream/80 max-w-2xl mx-auto">
          Try LISTING AiGENT for a full week for the price of a coffee. Upgrade only if you love it.
        </p>
      </div>
    </section>
  );
}

const VARIANT_STYLES = {
  light: {
    container: 'relative p-10 rounded-3xl bg-white border border-brand-cream-dark flex flex-col',
    eyebrow: 'text-brand-green/60',
    title: 'text-brand-green',
    price: 'text-brand-green',
    suffix: 'text-brand-green/60',
    desc: 'text-brand-green/70',
    check: 'text-brand-green',
    featText: 'text-brand-green/80',
    cta: 'mt-10 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg border border-brand-green text-brand-green font-semibold hover:bg-brand-green hover:text-brand-cream transition-colors duration-200',
  },
  dark: {
    container: 'relative p-10 rounded-3xl bg-brand-green text-brand-cream flex flex-col shadow-2xl',
    eyebrow: 'text-brand-gold',
    title: 'text-brand-cream',
    price: 'text-brand-cream',
    suffix: 'text-brand-cream/70',
    desc: 'text-brand-cream/70',
    check: 'text-brand-gold',
    featText: 'text-brand-cream/90',
    cta: 'mt-10 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-cream text-brand-green font-semibold hover:scale-[1.02] transition-transform',
  },
};

function PricingBadge({ label }) {
  if (!label) return null;
  return (
    <span className="absolute -top-3 right-8 px-3 py-1 rounded-full bg-brand-gold text-brand-green-darker text-xs font-semibold tracking-wider uppercase flex items-center gap-1">
      <Sparkles className="w-3 h-3" /> {label}
    </span>
  );
}

function PricingFeatureList({ features, checkClass, textClass }) {
  return (
    <ul className="mt-8 space-y-3">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <Check className={`w-5 h-5 ${checkClass} mt-0.5 shrink-0`} />
          <span className={textClass}>{f}</span>
        </li>
      ))}
    </ul>
  );
}

function PricingCard({
  eyebrow, title, price, priceSuffix, description,
  features, ctaLabel, ctaTo, variant = 'light', badge,
}) {
  const s = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  return (
    <div className={s.container}>
      <PricingBadge label={badge} />
      <span className={`text-xs uppercase tracking-[0.2em] ${s.eyebrow}`}>{eyebrow}</span>
      <h3 className={`mt-3 font-serif text-3xl ${s.title}`}>{title}</h3>
      <div className="mt-6 flex items-baseline gap-2">
        <span className={`font-serif text-6xl ${s.price}`}>{price}</span>
        <span className={s.suffix}>{priceSuffix}</span>
      </div>
      <p className={`mt-4 ${s.desc}`}>{description}</p>
      <PricingFeatureList features={features} checkClass={s.check} textClass={s.featText} />
      <Link to={ctaTo} className={s.cta}>
        {ctaLabel} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function PricingDisclaimer() {
  return (
    <p className="mt-10 text-center text-sm text-brand-green/60 max-w-2xl mx-auto">
      After your 7-day trial ends, your subscription automatically renews at $100/month, charged each month until you cancel. Cancel anytime before the trial ends to avoid the monthly charge — cancellation takes effect at the end of the current billing period.
    </p>
  );
}

export default function Pricing() {
  return (
    <div className="bg-brand-cream">
      <PricingHero />
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <PricingCard
              eyebrow="7-day trial"
              title="Activation"
              price="$10"
              priceSuffix="one-time"
              description="Unlocks full LISTING AiGENT for 7 days, then renews at $100/month unless you cancel."
              features={TRIAL_FEATURES}
              ctaLabel="Start trial"
              ctaTo="/download"
              variant="light"
            />
            <PricingCard
              eyebrow="Monthly plan"
              title="AiGENT Pro"
              price="$100"
              priceSuffix="/ month"
              description="Full functionality after your trial. Cancel anytime."
              features={PRO_FEATURES}
              ctaLabel="Upgrade after trial"
              ctaTo="/contact"
              variant="dark"
              badge="Most Popular"
            />
          </div>
          <PricingDisclaimer />
        </div>
      </section>
    </div>
  );
}
